<script lang="ts">
	import type { MenuItem } from "../../../../core/schema/MenuItem.js";
	import arrowRight from "../../../../core/assets/icons/arrow-right-long.svg?raw";
	import Icon from "../Icon.svelte";

	export let activeItem: MenuItem | null;
	// Opens via CSS :hover/:focus-within of the parent <li> instead of activeItem (pre-hydration fallback)
	export let cssHover = false;

	let displayed: MenuItem | null = null;

	$: isOpen = !cssHover && activeItem !== null;
	$: if (activeItem) {
		displayed = activeItem;
	}
	$: submenus = displayed?.submenus ?? [];
	$: gridColumns = `24rem repeat(${submenus.length}, minmax(0, 1fr))`;
</script>

<div class="submenu" class:is-open={isOpen} class:css-hover={cssHover} inert={!isOpen && !cssHover}>
	{#if displayed}
		{#key displayed.id}
			<div class="content grid items-start" style:grid-template-columns={gridColumns}>
				<!-- menu item name + desc -->
				<div class="bg-ltwhiteout border-lttertiary flex min-h-[15.625rem] w-96 flex-col gap-4 border p-8">
					<a
						href="/{displayed.path}"
						class="text-ltprimary hover:text-ltprimary-300 text-2md/none font-['Roboto'] font-normal uppercase transition-colors duration-200"
					>
						{displayed.name}
					</a>
					{#if displayed.description}
						<p class="text-xs font-normal">{displayed.description}</p>
					{/if}
				</div>

				<!-- submenu columns -->
				{#each submenus as submenuItem}
					<div
						class="bg-ltwhiteout border-lttertiary hover:bg-lttertiary-100 flex flex-col border transition-colors duration-200"
					>
						{#if submenuItem.name}
							{#if submenuItem.path}
								<a
									href="/{submenuItem.path}"
									class="border-lttertiary hover:text-ltprimary-300 min-h-[calc(2lh+2rem)] border-b px-8 py-4 text-base font-semibold uppercase tracking-[0.05rem] text-black transition-colors"
								>
									{submenuItem.name}
								</a>
							{:else}
								<span
									class="border-lttertiary min-h-[calc(2lh+2rem)] border-b px-8 py-4 text-base font-semibold uppercase tracking-[0.05rem] text-black"
								>
									{submenuItem.name}
								</span>
							{/if}
						{/if}
						<div class="flex flex-col">
							{#each submenuItem.items ?? [] as item}
								<a
									class="border-lttertiary hover:bg-lttertiary group relative flex min-h-[3.25rem] cursor-pointer items-center border-b px-6 py-2 text-sm/tight text-black transition-colors duration-200 last:border-b-0"
									href="/{item.path}{item.anchor ? `#${item.anchor}` : ''}"
									target={item.target}
								>
									<span class="mr-3">{item.name}</span>
									<Icon
										icon={arrowRight}
										class="text-ltprimary-300 ml-auto size-4 shrink-0 opacity-0 transition-opacity duration-200 group-hover:opacity-100"
									/>
								</a>
							{/each}
						</div>
					</div>
				{/each}
			</div>
		{/key}
	{/if}
</div>

<style lang="postcss">
	.submenu {
		@apply pointer-events-none fixed left-0 top-full z-50 w-full min-w-full -translate-y-2 cursor-default whitespace-normal opacity-0 transition-all;
	}

	.submenu::before {
		@apply absolute -top-4 left-0 h-4 w-full content-[''];
	}

	.submenu.is-open,
	:global(li:hover) .submenu.css-hover,
	:global(li:focus-within) .submenu.css-hover {
		@apply pointer-events-auto translate-y-0 opacity-100;
	}

	.content :is(p, span, a:not(:has(span))) {
		animation: fade-in 150ms ease-out;
	}

	@keyframes fade-in {
		from {
			opacity: 0;
		}
	}
</style>
